import { forwardRef, useImperativeHandle } from 'react';

import {
  Enquiries_Source_Enum_Enum,
  type GetDashboardNewEnquiriesQuery,
  type GetDashboardNewEnquiriesQueryVariables,
} from '@graphql/__generated__/graphql';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { DashboardEnquiriesCardItem } from '@modules/dashboard/dashboard-enquiry/DashboardEnquiriesCardItem';
import { GET_DASHBOARD_NEW_ENQUIRIES } from '@modules/dashboard/dashboardQueries';
import { useDashboardList } from '@modules/dashboard/hooks/useDashboardList';
import { DashboardCard } from '@modules/dashboard/shared/DashboardCard';
import { type RetryableRef } from '@modules/dashboard/types';
import { useLocale } from '@shared/hooks/locale';

export const DashboardEnquiriesCard = forwardRef<RetryableRef, object>(
  (_props, ref) => {
    const { t } = useLocale();
    const { me } = useAppData();

    const queryResult = useDashboardList<
      GetDashboardNewEnquiriesQuery,
      GetDashboardNewEnquiriesQueryVariables,
      GetDashboardNewEnquiriesQuery['enquiries'][number]
    >({
      query: GET_DASHBOARD_NEW_ENQUIRIES,
      variables: {
        where: {
          lot: { broker_id: { _eq: me?.id } },
          source: {
            _in: [
              Enquiries_Source_Enum_Enum.Portal,
              Enquiries_Source_Enum_Enum.Unknown,
            ],
          },
          processed: { _eq: false },
        },
      },
      dataKey: 'enquiries',
      countKey: 'enquiries_aggregate',
      fetchPolicy: 'network-only',
    });

    useImperativeHandle(
      ref,
      () => ({
        retry: async () => {
          await queryResult.refetch();
        },
        openEmailDialog: () => {
          // Not implemented for enquiries
        },
      }),
      [queryResult],
    );

    return (
      <DashboardCard
        title={t('New enquiries')}
        actionLabel={t('View all enquiries')}
        actionLink={`/enquiries?where=${JSON.stringify({
          source: { _in: ['portal'] },
        })}`}
        queryResult={queryResult}
        renderItem={(enquiry, key) => (
          <DashboardEnquiriesCardItem key={key} enquiry={enquiry} />
        )}
        emptyStateMessage={t('You do not have any new enquiries to process')}
        emptyStateVariant="success"
      />
    );
  },
);
